import React from "react";
import Paragraph from "../paragraph/Paragraph";
import Button from "../button/Button";
import { ReactComponent as ConfirmIcon } from "../../assets/icons/confirm.svg";
import { trim } from "../../utils/trim";
import { useCheckoutSubscription } from "../../hooks/useCheckoutSubscription";
import { useCheckoutOneTime } from "../../hooks/useCheckoutOneTime";
import { useChangeSubscription } from "../../hooks/useChangeSubscription";
import { convertFromISO8601 } from "../../utils/convertToIso8601";
import { ROUTES } from "../../constants/routes";
import { useLocation, useNavigate } from "react-router-dom";
import {
  SubscriptionDetails,
  SubscriptionPriceType,
} from "../../types";
import { useCancelSubscription } from "../../hooks/useCancelSubscription";
import useAuthPersistStore from "../../store/authPersistStore";
import useSubscriptionStore from "../../store/subscriptionStore";
import { useExtendSubscription } from "../../hooks/useExtendSubscription";

interface SubscriptionPlanProps {
  title: string;
  subtitle: string;
  description: string;
  items: { checked: boolean; text: string }[];
  price?: string;
  discount?: number;
  isActive: boolean;
  duration?: string | null;
  subscriptionType: string;
  subscriptionDetails: SubscriptionDetails | undefined;
  subscriptionPrice: SubscriptionPriceType | undefined;
  isOneTime?: boolean;
}

const SubscriptionPlan: React.FC<SubscriptionPlanProps> = ({
  title,
  subtitle,
  description,
  items,
  price,
  subscriptionType,
  subscriptionDetails,
  subscriptionPrice,
  discount,
  isOneTime = false,
}) => {
  const {
    mutate: checkoutSubscriptionMutate,
    isLoading: isCheckoutSubscriptionLoading,
  } = useCheckoutSubscription();
  const {
    mutate: checkoutOneTimeMutate,
    isLoading: isCheckoutOneTimeLoading,
  } = useCheckoutOneTime();

  // Jednokratni planovi idu na /checkout-for-one-time-access, pretplata na /checkout-for-subscription
  const checkoutSubscription = isOneTime
    ? checkoutOneTimeMutate
    : checkoutSubscriptionMutate;
  const isCheckoutLoading = isOneTime
    ? isCheckoutOneTimeLoading
    : isCheckoutSubscriptionLoading;
  const { mutate: extendSubscription, isLoading: isExtendSubscriptionLoading } =
    useExtendSubscription();
  const { setSubscriptionType } = useSubscriptionStore();
  const { mutate: changeSubscription, isLoading: isChangeSubscriptionLoading } =
    useChangeSubscription();
  const { mutate: cancelSubscription, isLoading: isCancelSubscriptionLoading } =
    useCancelSubscription();
  const { isAuthenticated } = useAuthPersistStore();
  const navigate = useNavigate();

  const location = useLocation();
  const isActive = subscriptionDetails?.subscriptionType === subscriptionType;
  const isNotActive = subscriptionDetails?.subscriptionType === undefined;
  const planScheduledToCancel = subscriptionDetails?.scheduledToCancel;

  const renderButtons = () => {
    if (isAuthenticated && planScheduledToCancel && isActive) {
      return (
        <Button
          variant="primary"
          label="Produži plan"
          hasLoader={true}
          isLoading={isExtendSubscriptionLoading}
          onClick={() => {
            if (!isAuthenticated) {
              navigate(ROUTES.login);
              return;
            }

            extendSubscription(
              {
                successUrl: `${process.env.REACT_APP_BASE_URL}${ROUTES.checkoutSuccess}?redirectTo=${location?.pathname}`,
                cancelUrl: window.location.href,
              },
              {
                onSuccess: (variable) => {
                  // @ts-ignore
                  const { checkoutLink } = variable;

                  if (checkoutLink) {
                    window.location.href = checkoutLink;
                  }
                },
              }
            );
          }}
        />
      );
    }

    if (isAuthenticated && isActive) {
      return (
        <div className="subscription-plan-item_owned">
          <Button
            variant="error"
            label="Otkaži plan"
            disabled={isCancelSubscriptionLoading}
            isLoading={isCancelSubscriptionLoading}
            hasLoader={true}
            onClick={() =>
              cancelSubscription(
                {
                  customerPortalFlowType: "subscription_cancel",
                  successUrl: `${process.env.REACT_APP_BASE_URL}${ROUTES.checkoutSuccess}?redirectTo=${location?.pathname}&type=subscription_cancel`,
                  cancelUrl: window.location.href,
                },
                {
                  onSuccess: (variable) => {
                    // @ts-ignore
                    const { checkoutLink } = variable;

                    if (checkoutLink) {
                      window.location.href = checkoutLink;
                    }
                  },
                }
              )
            }
          />
          <Button
            variant="white-outlined"
            label="Promeni detalje plaćanja"
            disabled={isChangeSubscriptionLoading}
            isLoading={isChangeSubscriptionLoading}
            hasLoader={true}
            onClick={() =>
              changeSubscription(
                {
                  customerPortalFlowType: "payment_method_update",
                  successUrl: `${process.env.REACT_APP_BASE_URL}${ROUTES.checkoutSuccess}?redirectTo=${location?.pathname}&type=payment_method_update`,
                  cancelUrl: window.location.href,
                },
                {
                  onSuccess: (variable) => {
                    // @ts-ignore
                    const { checkoutLink } = variable;

                    if (checkoutLink) {
                      window.location.href = checkoutLink;
                    }
                  },
                }
              )
            }
          />
        </div>
      );
    }

    const buttonLabel = isOneTime
      ? "Kupi"
      : "Započni svoj plan";
    
    return (
      <Button
        variant="primary"
        label={buttonLabel} //zavisno od plana, pisace odgovarajuci tekst
        disabled={!isNotActive}
        hasLoader={true}
        isLoading={isCheckoutLoading}
        onClick={() => {
          if (!isAuthenticated) {
            setSubscriptionType({ subscriptionType });
            navigate(ROUTES.login);
            return;
          }

          checkoutSubscription(
            {
              ...(isOneTime
                ? { oneTimeAccessType: subscriptionType }
                : { subscriptionType }),
              successUrl: `${process.env.REACT_APP_BASE_URL}${ROUTES.checkoutSuccess}?redirectTo=${location?.pathname}`,
              cancelUrl: window.location.href,
            },
            {
              onSuccess: (variable) => {
                // @ts-ignore
                const { checkoutLink } = variable;

                if (checkoutLink) {
                  window.location.href = checkoutLink;
                }
              },
            }
          );
        }}
      />
    );
  };

  return (
    <div
      className={trim(
        `subscription-plan ${isActive ? "subscription-plan--active" : ""}`
      )}
    >
      <div className="subscription-plan_header">
        <div className="subscription-plan_tags">
          <span
            className={trim(
              `subscription-plan_badge ${
                isOneTime
                  ? "subscription-plan_badge--onetime"
                  : "subscription-plan_badge--subscription"
              }`
            )}
          >
            {isOneTime ? "Jednokratno" : "Pretplata"}
          </span>
          {discount && (
            <div className="subscription-plan_discount">
              <Paragraph variant="title">Popust {discount}€</Paragraph>
            </div>
          )}
          {isActive && (
            <div className="subscription-plan_subscribed">
              <ConfirmIcon />
            </div>
          )}
        </div>
        <h2 className="subscription-plan_title">{title}</h2>
      </div>
      <div className="subscription-plan_price">
        <span className="subscription-plan_price-amount">
          {subscriptionPrice
            ? `${Math.round(subscriptionPrice?.amount / 100)}`
            : "--"}
          €
        </span>
        <span className="subscription-plan_price-suffix">
          {isOneTime ? `za ${subtitle}` : `/ ${subtitle}`}
        </span>
      </div>
      {subscriptionDetails?.currentPeriodActiveUntil && isActive && (
        <>
          <div className="subscription-plan_subscription">
            <Paragraph variant="p2">
              {planScheduledToCancel ? "otkazivanje" : "obnova"}{" "}
              {convertFromISO8601(
                subscriptionDetails?.currentPeriodActiveUntil
              )}
              .
            </Paragraph>
          </div>
        </>
      )}
      <Paragraph variant="p2" className="subscription-plan_description">
        {description}
      </Paragraph>
      <span className="subscription-plan_divider" />
      <ul className="subscription-plan_plan">
        {items.map(({ checked, text }) => (
          <div
            className={trim(
              `subscription-plan-item ${
                checked && "subscription-plan-item--checked"
              }`
            )}
            key={text}
          >
            <div className="subscription-plan-item_indicator">
              {checked && <ConfirmIcon />}
            </div>
            <p>{text}</p>
          </div>
        ))}
      </ul>
      {renderButtons()}
    </div>
  );
};

export default SubscriptionPlan;
